import { SUPPORTED_UI_LOCALES } from '@kiao-voice/core'

/**
 * Locale resolution for the Next.js web app.
 *
 * Server components read `Accept-Language` and the `kiao-ui-locale` cookie,
 * then resolve to a single supported locale. Cookie wins over header.
 *
 * This module is pure (no React, no `next/*` imports) so it can be unit
 * tested without mocking Next runtime primitives.
 */

export type WebLocale = (typeof SUPPORTED_UI_LOCALES)[number]

export const UI_LOCALE_COOKIE = 'kiao-ui-locale'
// Primary market is Taiwan; default to zh-TW so first-time visitors who
// don't send an Accept-Language header (or whose browser is set to a
// language we don't support) still get Traditional Chinese, not English.
// Users with a clear Accept-Language preference (en-US, etc.) are still
// honoured via parseAcceptLanguage() above.
export const DEFAULT_WEB_LOCALE: WebLocale = 'zh-TW'

/** Year-long cookie lifetime; re-set on every toggle. */
export const UI_LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function isSupportedLocale(value: string): value is WebLocale {
  return (SUPPORTED_UI_LOCALES as readonly string[]).includes(value)
}

/**
 * Match a single BCP-47 tag against the supported locales.
 * Tries an exact match first, then matches on the base language subtag
 * (so `zh-CN` and `zh-HK` both resolve to `zh-TW`).
 */
function matchLocale(tag: string): WebLocale | null {
  const normalized = tag.trim()
  if (!normalized) return null

  // Exact match, case-insensitive on region (en-US vs en-us).
  const exact = SUPPORTED_UI_LOCALES.find(
    (locale) => locale.toLowerCase() === normalized.toLowerCase()
  )
  if (exact) return exact

  // Base-language prefix match (zh-* → zh-TW, en-* → en).
  const baseLang = normalized.split('-')[0].toLowerCase()
  const prefix = SUPPORTED_UI_LOCALES.find(
    (locale) => locale.split('-')[0].toLowerCase() === baseLang
  )
  return prefix ?? null
}

/**
 * Walk an `Accept-Language` header in the order it was provided and return
 * the first tag that matches a supported locale. Quality weights are stripped
 * but not sorted — matches the API middleware's behavior for consistency.
 */
function parseAcceptLanguage(header: string | null): WebLocale | null {
  if (!header) return null
  const entries = header.split(',')
  for (const entry of entries) {
    const tag = entry.split(';')[0]
    const matched = matchLocale(tag)
    if (matched) return matched
  }
  return null
}

export interface ResolveLocaleInput {
  acceptLanguage: string | null
  cookieValue: string | null
}

/**
 * Resolve the active UI locale for a request.
 *
 * Priority:
 *  1. Validated `kiao-ui-locale` cookie (exact match only — cookies are set
 *     by us, so unknown values are discarded).
 *  2. First supported tag from `Accept-Language` (exact or base-lang prefix).
 *  3. `DEFAULT_WEB_LOCALE` (zh-TW — primary market is Taiwan).
 */
export function resolveLocale({ acceptLanguage, cookieValue }: ResolveLocaleInput): WebLocale {
  if (cookieValue && isSupportedLocale(cookieValue)) {
    return cookieValue
  }
  return parseAcceptLanguage(acceptLanguage) ?? DEFAULT_WEB_LOCALE
}
